<script setup>
import { useAbility } from '@casl/vue'

import { cookieRef } from '@/@layouts/stores/config'

import TagService from '@/services/tag.service'

definePage({
  meta: {
    name: 'app-quality-document-masters-categories',
    subject: 'document.menu',
    action: 'masters',
  },
})

const { can } = useAbility()
const selectedUnitId = cookieRef('selectedUnit')

const search = ref('')
const debouncedSearch = useDebounceFn(value => { search.value = value }, 500)

const {
  data: tagsData,
  execute: fetchTags,
  isFetching,
} = await useApi(createUrl('/masters/tags', {
  query: {
    paged: 1,
    q: search,
    search: { tag_type: 'DOCUMENT_CATEGORY', unit_id: selectedUnitId },
  },
}))

const categories = computed(() => tagsData.value?.data ?? [])

const isDialogOpen = ref(false)
const editing = ref(null)

// status is required|boolean on StoreTagRequest — a payload without it 422s.
// Kept in `form` (not bolted on only at the call site) so the shape a
// successful create needs is visible here, matching the default
// `views/masters/tag/Drawer.vue` uses.
const blankForm = () => ({ name: '', status: 1 })

const form = ref(blankForm())

const openCreate = () => {
  editing.value = null
  form.value = blankForm()
  isDialogOpen.value = true
}

const openEdit = tag => {
  editing.value = tag
  form.value = { name: tag.name, status: tag.status ?? 1 }
  isDialogOpen.value = true
}

const submit = async () => {
  const done = () => {
    isDialogOpen.value = false
    fetchTags()
  }

  // tags.unit_id and tags.user_id are both NOT NULL, so unit_id must be sent.
  if (editing.value)
    await TagService.updateTag(editing.value.id, { ...form.value, tag_type: 'DOCUMENT_CATEGORY' }, done)
  else
    await TagService.addTag({ ...form.value, tag_type: 'DOCUMENT_CATEGORY', unit_id: selectedUnitId.value }, done)
}
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap gap-3 mb-6">
      <div class="text-h5 flex-grow-1">
        Document Categories
      </div>
      <AppTextField
        placeholder="Search..."
        density="compact"
        style="inline-size: 240px;"
        @input="debouncedSearch($event.target.value)"
      />
      <VBtn
        v-if="can('configure', 'quality.document')"
        prepend-icon="tabler-plus"
        @click="openCreate"
      >
        New category
      </VBtn>
    </div>

    <VProgressLinear
      v-if="isFetching"
      indeterminate
      color="primary"
      class="mb-3"
    />

    <VCard
      variant="flat"
      class="border"
    >
      <VList>
        <VListItem
          v-for="tag in categories"
          :key="tag.id"
        >
          <template #prepend>
            <VIcon
              icon="tabler-tag"
              class="me-3"
            />
          </template>
          <VListItemTitle>{{ tag.name }}</VListItemTitle>
          <template #append>
            <VBtn
              v-if="can('configure', 'quality.document')"
              icon="tabler-edit"
              variant="text"
              size="small"
              @click="openEdit(tag)"
            />
          </template>
        </VListItem>
        <VListItem v-if="!categories.length && !isFetching">
          <VListItemTitle class="text-medium-emphasis">
            No categories yet.
          </VListItemTitle>
        </VListItem>
      </VList>
    </VCard>

    <VDialog
      v-model="isDialogOpen"
      max-width="460"
    >
      <VCard>
        <DialogCloseBtn @click="isDialogOpen = false" />
        <VCardText>
          <div class="text-h6 mb-4">
            {{ editing ? 'Edit category' : 'New category' }}
          </div>
          <AppTextField
            v-model="form.name"
            label="Name"
            required
          />
          <div class="d-flex gap-3 mt-6">
            <VBtn @click="submit">
              Save
            </VBtn>
            <VBtn
              variant="tonal"
              color="secondary"
              @click="isDialogOpen = false"
            >
              Cancel
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
